<!--
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import type { IntlString } from '@hcengineering/platform'
  import Label from './Label.svelte'

  export let label: IntlString | undefined = undefined
  export let width: string | undefined = undefined
  export let value: string | undefined = undefined
  export let error: string | undefined = undefined
  export let password: boolean | undefined = undefined
  export let id: string | undefined = undefined
  export let name: string | undefined = undefined
  export let disabled: boolean = false
</script>

<div class="editbox{error ? ' error' : ''}" style={width ? 'width: ' + width : ''}>
  {#if password}
    <input
      type="password"
      class:nolabel={!label}
      {id}
      {name}
      bind:value
      on:blur
      on:change
      on:keyup
      on:input
      autocomplete="off"
      placeholder=" "
      spellcheck="false"
      autocapitalize="off"
      {disabled}
    />
  {:else}
    <input
      type="text"
      class:nolabel={!label}
      {id}
      {name}
      bind:value
      on:blur
      on:change
      on:keyup
      on:input
      autocomplete="off"
      placeholder=" "
      spellcheck="false"
      autocapitalize="off"
      {disabled}
    />
  {/if}
  {#if label}
    <div class="label"><Label {label} /></div>
  {/if}
</div>

<style lang="scss">
  // THE input recipe: glass fill, emphasis border, cyan border swap on focus,
  // mono field label.
  .editbox {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    min-width: 3rem;
    height: 3.25rem;
    background-color: var(--glass-04);
    border: 1px solid var(--border-white-10);
    border-radius: var(--large-BorderRadius);
    caret-color: var(--accent);
    transition:
      border-color var(--duration-fast) var(--timing-standard),
      background-color var(--duration-fast) var(--timing-standard);

    &:focus-within {
      background-color: var(--glass-04);
      border-color: var(--focus-border);
    }

    input {
      height: 100%;
      margin: 0;
      padding: 1.125rem 1rem 0;
      font-size: 0.875rem;
      color: var(--text);
      background-color: transparent;
      border: none;
      border-radius: inherit;

      &::placeholder {
        color: var(--text-dim);
      }
      &:disabled {
        background-color: transparent;
        opacity: 0.5;
      }
    }

    .nolabel {
      padding-top: 0;
    }

    .label {
      position: absolute;
      top: 1.125rem;
      left: 1rem;
      font-family: var(--mono-font);
      font-size: 0.75rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      color: var(--text-dim);
      transition:
        top var(--duration-base) var(--timing-standard),
        font-size var(--duration-base) var(--timing-standard);
      pointer-events: none;
      -webkit-user-select: none;
      user-select: none;
    }
    input:focus + .label,
    input:not(:placeholder-shown) + .label {
      top: 0.5rem;
      font-size: 0.6875rem;
    }
  }
  .error {
    border: 1px solid rgba(var(--destructive-rgb) / 0.5);
    &:focus-within {
      border-color: var(--destructive);
    }
  }
</style>
